// ── Ship part definitions ─────────────────────────────────────────────────────
//
// Each part has 5 tiers: 0=common … 4=legendary.
// effects: stat deltas applied ON TOP of DEFAULT_STATS when this tier is equipped.
//   All values are additive unless the key ends in "Mult" (then multiplicative).
//   Tier 0 (common) = default ship baseline, so effects are all zero / neutral.
//
// Drop weights by rarity — higher tier = rarer drop.
// Base part drop chance per debris/kill event is configured in PART_DROP_CHANCE.

export const PART_DROP_CHANCE = {
  mine:  0.008, // ~0.8% per debris destroyed
  kill:  0.04,  // ~4% per enemy killed
};

// Weight of each rarity within a part drop roll
export const PART_RARITY_WEIGHTS = {
  common:    60,
  uncommon:  25,
  rare:      10,
  epic:       4,
  legendary:  1,
};

export const RARITY_COLOR = {
  common:    "#888888",
  uncommon:  "#44dd88",
  rare:      "#4488ff",
  epic:      "#aa44ff",
  legendary: "#ffaa00",
};
export const RARITY_LABEL = {
  common:    "COMMON",
  uncommon:  "UNCOMMON",
  rare:      "RARE",
  epic:      "EPIC",
  legendary: "LEGENDARY",
};
export const RARITY_ORDER = ["common", "uncommon", "rare", "epic", "legendary"];

export const SHIP_PARTS = [
  {
    id: "laser",
    name: "Mining Laser",
    slot: "WEAPON",
    icon: "⚡",
    color: "#00ccff",
    position: { x: 50, y: 18 },
    desc: "Primary harvesting tool. Higher damage breaks debris faster.",
    stats: [
      { key: "laserDamage", label: "Damage",     fmt: v => String(Math.round(v)) },
      { key: "laserRange",  label: "Range",      fmt: v => Math.round(v) + "px" },
      { key: "beamWidth",   label: "Beam Width", fmt: v => "×" + v.toFixed(2) },
      { key: "rotSpeed",    label: "Turn Speed", fmt: v => v.toFixed(3) },
    ],
    tiers: [
      { name: "Starter Laser",      rarity: "common",    icon: "⚡", color: "#888",
        desc: "Basic salvage laser. Gets the job done.",
        effects: {} },
      { name: "Pulse Cutter",       rarity: "uncommon",  icon: "⚡", color: "#44dd88",
        desc: "+20% damage. Pulsed beam cuts faster.",
        effects: { laserDamage: 2, rotSpeed: 0.015 } },
      { name: "Focused Array",      rarity: "rare",      icon: "⚡", color: "#4488ff",
        desc: "+50% damage, +25px range.",
        effects: { laserDamage: 5, laserRange: 25 } },
      { name: "Ion Slicer",         rarity: "epic",      icon: "⚡", color: "#aa44ff",
        desc: "+100% damage, wider beam, faster rotation.",
        effects: { laserDamage: 9, laserRange: 40, beamWidth: 0.4, rotSpeed: 0.03 } },
      { name: "Plasma Annihilator", rarity: "legendary", icon: "⚡", color: "#ffaa00",
        desc: "Maximum output. Vaporizes debris instantly.",
        effects: { laserDamage: 18, laserRange: 65, beamWidth: 0.8, rotSpeed: 0.055 } },
    ],
  },
  {
    id: "hull",
    name: "Hull Plating",
    slot: "ARMOR",
    icon: "🛡️",
    color: "#8899aa",
    position: { x: 50, y: 48 },
    desc: "Structural integrity of the ship. Absorbs direct impact damage.",
    stats: [
      { key: "playerMaxHp", label: "Max HP",     fmt: v => String(Math.round(v)) },
      { key: "autoBreak",   label: "Auto-Decay", fmt: v => v > 0 ? Math.round(v * 100) + "%" : "OFF" },
    ],
    tiers: [
      { name: "Salvage Shell",      rarity: "common",    icon: "🛡️", color: "#888",
        desc: "Patched hull scavenged from wrecks.",
        effects: {} },
      { name: "Reinforced Panels",  rarity: "uncommon",  icon: "🛡️", color: "#44dd88",
        desc: "+25 HP. Titanium-weave composite.",
        effects: { playerMaxHp: 25 } },
      { name: "Ablative Plating",   rarity: "rare",      icon: "🛡️", color: "#4488ff",
        desc: "+60 HP. Deflects glancing shots.",
        effects: { playerMaxHp: 60 } },
      { name: "Reactive Armor",     rarity: "epic",      icon: "🛡️", color: "#aa44ff",
        desc: "+120 HP. Absorbs and redistributes impact.",
        effects: { playerMaxHp: 120 } },
      { name: "Nano-Hull Matrix",   rarity: "legendary", icon: "🛡️", color: "#ffaa00",
        desc: "+200 HP. Self-sealing nano-fiber lattice.",
        effects: { playerMaxHp: 200 } },
    ],
  },
  {
    id: "shield",
    name: "Shield Generator",
    slot: "DEFENSE",
    icon: "🔰",
    color: "#44aaff",
    position: { x: 50, y: 33 },
    desc: "Projects an energy barrier that absorbs hits before hull takes damage.",
    stats: [
      { key: "shieldMax", label: "Shield HP", fmt: v => v > 0 ? String(Math.round(v)) : "NONE" },
    ],
    tiers: [
      { name: "No Shield",        rarity: "common",    icon: "🔰", color: "#555",
        desc: "Unequipped. No energy barrier.",
        effects: {} },
      { name: "Deflector Bubble", rarity: "uncommon",  icon: "🔰", color: "#44dd88",
        desc: "+30 shield. Basic energy barrier.",
        effects: { shieldMax: 20 } },
      { name: "Kinetic Barrier",  rarity: "rare",      icon: "🔰", color: "#4488ff",
        desc: "+80 shield. Faster recharge rate.",
        effects: { shieldMax: 70 } },
      { name: "Void Shroud",      rarity: "epic",      icon: "🔰", color: "#aa44ff",
        desc: "+160 shield. Partial reflection on hit.",
        effects: { shieldMax: 150 } },
      { name: "Quantum Aegis",    rarity: "legendary", icon: "🔰", color: "#ffaa00",
        desc: "+280 shield. Regenerates even in combat.",
        effects: { shieldMax: 270 } },
    ],
  },
  {
    id: "engine",
    name: "Engine Core",
    slot: "PROPULSION",
    icon: "🚀",
    color: "#ff9944",
    position: { x: 50, y: 80 },
    desc: "Drives the ship's movement speed across the battlefield.",
    stats: [
      { key: "shipSpeed", label: "Speed", fmt: v => v.toFixed(2) },
    ],
    tiers: [
      { name: "Ion Drive",        rarity: "common",    icon: "🚀", color: "#888",
        desc: "Standard sublight propulsion.",
        effects: {} },
      { name: "Boosted Thruster", rarity: "uncommon",  icon: "🚀", color: "#44dd88",
        desc: "+15% speed. Magnetic plasma injection.",
        effects: { shipSpeed: 0.27 } },
      { name: "Warp Nacelle",     rarity: "rare",      icon: "🚀", color: "#4488ff",
        desc: "+30% speed. Micro-warp capability.",
        effects: { shipSpeed: 0.54 } },
      { name: "Gravitic Drive",   rarity: "epic",      icon: "🚀", color: "#aa44ff",
        desc: "+55% speed. Bends local spacetime.",
        effects: { shipSpeed: 0.99 } },
      { name: "Singularity Core", rarity: "legendary", icon: "🚀", color: "#ffaa00",
        desc: "+90% speed. Near-relativistic bursts.",
        effects: { shipSpeed: 1.62 } },
    ],
  },
  {
    id: "tractor",
    name: "Tractor Beam",
    slot: "UTILITY",
    icon: "🧲",
    color: "#44ffcc",
    position: { x: 72, y: 28 },
    desc: "Pulls in debris and wreck loot automatically.",
    stats: [
      { key: "attractRange", label: "Pull Range", fmt: v => Math.round(v) + "px" },
      { key: "bonusLoot",    label: "Bonus Loot", fmt: v => "+" + Math.round(v) },
    ],
    tiers: [
      { name: "Magnetic Clip",   rarity: "common",    icon: "🧲", color: "#888",
        desc: "Short-range loot magnet.",
        effects: {} },
      { name: "Gravity Snare",   rarity: "uncommon",  icon: "🧲", color: "#44dd88",
        desc: "+40px range. Catches fast debris.",
        effects: { attractRange: 40 } },
      { name: "Void Funnel",     rarity: "rare",      icon: "🧲", color: "#4488ff",
        desc: "+100px range, +1 bonus loot.",
        effects: { attractRange: 100, bonusLoot: 1 } },
      { name: "Singularity Net", rarity: "epic",      icon: "🧲", color: "#aa44ff",
        desc: "+200px range, +2 bonus loot.",
        effects: { attractRange: 200, bonusLoot: 2 } },
      { name: "Dark Pull Array", rarity: "legendary", icon: "🧲", color: "#ffaa00",
        desc: "+350px range, +4 bonus loot. Nothing escapes.",
        effects: { attractRange: 350, bonusLoot: 4 } },
    ],
  },
  {
    id: "blaster",
    name: "Blaster Cannon",
    slot: "COMBAT",
    icon: "🔫",
    color: "#ff4455",
    position: { x: 28, y: 28 },
    desc: "Ship-mounted combat weapon for destroying enemy vessels.",
    stats: [
      { key: "blasterDamage",   label: "Damage",     fmt: v => String(Math.round(v)) },
      { key: "blasterFireRate", label: "Fire Rate",  fmt: v => String(Math.round(v)) + " fr" },
      { key: "blasterCount",    label: "Shots/Fire", fmt: v => String(Math.round(v)) },
    ],
    tiers: [
      { name: "Point Defense Gun", rarity: "common",    icon: "🔫", color: "#888",
        desc: "Single-shot deterrent.",
        effects: {} },
      { name: "Pulse Repeater",    rarity: "uncommon",  icon: "🔫", color: "#44dd88",
        desc: "+25% damage. Higher rate of fire.",
        effects: { blasterDamage: 3, blasterFireRate: -4 } },
      { name: "Twin Cannons",      rarity: "rare",      icon: "🔫", color: "#4488ff",
        desc: "+50% damage. Dual barrel spread.",
        effects: { blasterDamage: 6, blasterFireRate: -7, blasterCount: 1 } },
      { name: "Plasma Howitzer",   rarity: "epic",      icon: "🔫", color: "#aa44ff",
        desc: "+90% damage. Charged plasma rounds.",
        effects: { blasterDamage: 11, blasterFireRate: -10, blasterCount: 1 } },
      { name: "Void Lance",        rarity: "legendary", icon: "🔫", color: "#ffaa00",
        desc: "+160% damage. Pierces through hulls.",
        effects: { blasterDamage: 20, blasterFireRate: -14, blasterCount: 2 } },
    ],
  },
  {
    id: "forge",
    name: "Forge Unit",
    slot: "SYSTEMS",
    icon: "🔥",
    color: "#ffcc33",
    position: { x: 72, y: 60 },
    desc: "Onboard smelter. Processes raw materials into refined components.",
    stats: [
      { key: "smeltSpeed",    label: "Speed",  fmt: v => "×" + v.toFixed(2) },
      { key: "smeltYield",    label: "Yield",  fmt: v => "×" + v.toFixed(2) },
      { key: "smeltCostMult", label: "Cost",   fmt: v => "×" + v.toFixed(2) },
      { key: "smeltQueueMax", label: "Queue",  fmt: v => Math.round(v) + " slots" },
    ],
    tiers: [
      { name: "Micro Crucible",  rarity: "common",    icon: "🔥", color: "#888",
        desc: "Basic single-slot smelter.",
        effects: {} },
      { name: "Arc Furnace",     rarity: "uncommon",  icon: "🔥", color: "#44dd88",
        desc: "+20% speed. Arc-plasma heating.",
        effects: { smeltSpeed: 0.2 } },
      { name: "Thermal Reactor", rarity: "rare",      icon: "🔥", color: "#4488ff",
        desc: "+45% speed, +10% yield.",
        effects: { smeltSpeed: 0.45, smeltYield: 0.1 } },
      { name: "Quantum Kiln",    rarity: "epic",      icon: "🔥", color: "#aa44ff",
        desc: "+90% speed, +25% yield, −10% cost.",
        effects: { smeltSpeed: 0.9, smeltYield: 0.25, smeltCostMult: -0.1 } },
      { name: "Void Foundry",    rarity: "legendary", icon: "🔥", color: "#ffaa00",
        desc: "Max throughput. +2 queue slots.",
        effects: { smeltSpeed: 1.5, smeltYield: 0.5, smeltCostMult: -0.2, smeltQueueMax: 2 } },
    ],
  },
  {
    id: "nav",
    name: "Nav Array",
    slot: "NAVIGATION",
    icon: "🗺️",
    color: "#33dd99",
    position: { x: 28, y: 60 },
    desc: "Navigation and sector scanning systems.",
    stats: [
      { key: "scannerCharges",  label: "Scanner Charges",  fmt: v => v > 0 ? String(Math.round(v)) : "NONE" },
      { key: "scannerReveal",   label: "Nodes Revealed",   fmt: v => String(Math.round(v)) },
      { key: "sectorDiversity", label: "Sector Diversity", fmt: v => String(Math.round(v)) },
    ],
    tiers: [
      { name: "Basic Transponder",  rarity: "common",    icon: "🗺️", color: "#888",
        desc: "Minimal navigation. No scanning.",
        effects: {} },
      { name: "Sector Mapper",      rarity: "uncommon",  icon: "🗺️", color: "#44dd88",
        desc: "+1 scanner charge. Reveals 1 node.",
        effects: { scannerCharges: 1, scannerReveal: 1 } },
      { name: "Deep Probe Array",   rarity: "rare",      icon: "🗺️", color: "#4488ff",
        desc: "+2 charges. Reveals 2 nodes per scan.",
        effects: { scannerCharges: 2, scannerReveal: 2, sectorDiversity: 1 } },
      { name: "Warp Cartographer",  rarity: "epic",      icon: "🗺️", color: "#aa44ff",
        desc: "+3 charges, 3 reveals. Sector biasing.",
        effects: { scannerCharges: 3, scannerReveal: 3, sectorDiversity: 2 } },
      { name: "Omniscient Grid",    rarity: "legendary", icon: "🗺️", color: "#ffaa00",
        desc: "Full sector visibility. Free fast travel.",
        effects: { scannerCharges: 5, scannerReveal: 5, sectorDiversity: 3, fastTravelFree: true } },
    ],
  },
  {
    id: "echo",
    name: "Echo Relay",
    slot: "RESONANCE",
    icon: "🌀",
    color: "#aa55ff",
    position: { x: 50, y: 63 },
    desc: "Resonance emitter that amplifies Rift Echo frequency and card power.",
    stats: [
      { key: "echoFrequency",      label: "Echo Freq",    fmt: v => "+" + Math.round(v * 100) + "%" },
      { key: "echoCurrencyBonus",  label: "Echo Bonus",   fmt: v => "+" + Math.round(v) },
      { key: "echoCardBoost",      label: "Echo Boost",   fmt: v => "+" + Math.round(v * 100) + "%" },
    ],
    tiers: [
      { name: "Null Emitter",       rarity: "common",    icon: "🌀", color: "#888",
        desc: "No resonance. Rift Echoes work at base power.",
        effects: {} },
      { name: "Rift Antenna",       rarity: "uncommon",  icon: "🌀", color: "#44dd88",
        desc: "+10% echo freq, +5% card boost.",
        effects: { echoFrequency: 0.1, echoCardBoost: 0.05 } },
      { name: "Void Resonator",     rarity: "rare",      icon: "🌀", color: "#4488ff",
        desc: "+25% freq, +15% boost, +1 echo/event.",
        effects: { echoFrequency: 0.25, echoCardBoost: 0.15, echoCurrencyBonus: 1 } },
      { name: "Echo Amplifier",     rarity: "epic",      icon: "🌀", color: "#aa44ff",
        desc: "+50% freq, +30% boost, +3 echo/event.",
        effects: { echoFrequency: 0.5, echoCardBoost: 0.3, echoCurrencyBonus: 3 } },
      { name: "Singularity Pulse",  rarity: "legendary", icon: "🌀", color: "#ffaa00",
        desc: "+100% freq, +60% boost. Void harmonics.",
        effects: { echoFrequency: 1.0, echoCardBoost: 0.6, echoCurrencyBonus: 6 } },
    ],
  },
];

// Map by id for fast lookup
export const SHIP_PART_MAP = Object.fromEntries(SHIP_PARTS.map(p => [p.id, p]));

// Flat weighted pool for random part + rarity rolls
export function rollPartDrop() {
  // Pick a random part
  const part = SHIP_PARTS[Math.floor(Math.random() * SHIP_PARTS.length)];

  // Pick a rarity by weight
  const total = Object.values(PART_RARITY_WEIGHTS).reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  let rarity = "common";
  for (const [rar, w] of Object.entries(PART_RARITY_WEIGHTS)) {
    r -= w;
    if (r <= 0) { rarity = rar; break; }
  }
  // tier index = rarity index
  const tierIdx = RARITY_ORDER.indexOf(rarity);
  return { partId: part.id, tierIdx };
}

// Compute the stat delta applied by the currently equipped tier of every part.
// Returns an object of { statKey: additiveBonus } (same shape as DEFAULT_STATS).
export function computeShipPartBonuses(equippedTiers) {
  const bonuses = {};
  for (const part of SHIP_PARTS) {
    const tierIdx = equippedTiers[part.id] ?? 0;
    const tier = part.tiers[tierIdx];
    if (!tier) continue;
    for (const [key, val] of Object.entries(tier.effects)) {
      bonuses[key] = (bonuses[key] ?? 0) + val;
    }
  }
  return bonuses;
}
